export type QuestTranslation = {
  title?: string;
  body?: string;
  excerpt?: string;
  summary?: string;
  role1_name?: string;
  role2_name?: string;
  role1_text?: string;
  role2_text?: string;
  warnings?: string[];
  hints?: string[];
};

function uniq(values: string[]) {
  const out: string[] = [];
  const seen = new Set<string>();

  for (const value of values) {
    const s = String(value || "").trim().toLowerCase();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }

  return out;
}

export function getPreferredQuestLocales(): string[] {
  const raw: string[] = [];

  try {
    const intlLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    if (intlLocale) raw.push(intlLocale);
  } catch {}

  try {
    if (typeof navigator !== "undefined") {
      const nav: any = navigator;
      if (nav.language) raw.push(nav.language);
      if (Array.isArray(nav.languages)) raw.push(...nav.languages);
    }
  } catch {}

  const expanded: string[] = [];
  for (const r of raw) {
    const full = String(r || "").replace(/_/g, "-").trim();
    if (!full) continue;
    expanded.push(full);
    const base = full.split("-")[0];
    if (base && base !== full) expanded.push(base);
  }

  expanded.push("en");
  return uniq(expanded);
}

export function safeParseMeta(meta: any): Record<string, any> {
  if (!meta) return {};
  if (typeof meta === "object") return meta;
  try {
    const parsed = JSON.parse(String(meta));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function pickQuestTranslation(meta: any, preferredLocales?: string[]): QuestTranslation | null {
  const parsed = safeParseMeta(meta);
  const translations = parsed?.translations;

  if (!translations || typeof translations !== "object") return null;

  const prefs = uniq(preferredLocales?.length ? preferredLocales : getPreferredQuestLocales());
  const keys = Object.keys(translations).map((k) => String(k).trim());

  for (const want of prefs) {
    const exact = keys.find((k) => k.toLowerCase() === want.toLowerCase());
    if (exact && translations[exact] && typeof translations[exact] === "object") {
      return translations[exact] as QuestTranslation;
    }

    const base = want.split("-")[0];
    const loose = keys.find((k) => k.toLowerCase().split("-")[0] === base);
    if (loose && translations[loose] && typeof translations[loose] === "object") {
      return translations[loose] as QuestTranslation;
    }
  }

  return null;
}

export function applyQuestTranslation<T extends Record<string, any>>(
  base: T,
  meta: any,
  preferredLocales?: string[]
): T & { _translationLocale?: string | null } {
  const tr = pickQuestTranslation(meta, preferredLocales);
  if (!tr) return { ...base, _translationLocale: null };

  const out: any = { ...base, _translationLocale: null };

  const maybeSetString = (srcKey: keyof QuestTranslation, dstKey?: string) => {
    const val = tr[srcKey];
    if (typeof val === "string" && val.trim()) {
      out[dstKey || srcKey] = val;
    }
  };

  maybeSetString("title");
  maybeSetString("body");
  maybeSetString("excerpt");
  maybeSetString("summary");
  maybeSetString("role1_name");
  maybeSetString("role2_name");
  maybeSetString("role1_text");
  maybeSetString("role2_text");

  if (Array.isArray(tr.warnings) && tr.warnings.length) {
    out.warnings = tr.warnings.filter((x) => typeof x === "string" && x.trim());
  }

  if (Array.isArray(tr.hints) && tr.hints.length) {
    out.hints = tr.hints.filter((x) => typeof x === "string" && x.trim());
  }

  return out;
}
